import { Dispatch, Reducer } from "react";
import { Category, RecommendedItem } from "./components/Category/contracts";
import bindActions from "./utility/bindActions";
import { User } from "~/common/Store/models/User";
import { logException } from "@wss/error-tracking";

type CategoryResponse = {
  categoryName: string;
  items: {
    description: string;
    imagePath: string;
    itemNumber: string;
    link: string;
    altText: string;
    price: number;
    salePrice: number;
    displaySalePrice: boolean;
    unitOfMeasure: string;
    isWebstaurantPlusEligible: boolean;
  }[];
};

// actions
const fetching = "fetching";
const fetched = "fetched";
const fetchError = "fetchError";

type State = {
  loading: boolean;
  categories: Category[];
  fetchError?: Error;
};

export const initialState: State = {
  loading: true,
  categories: [],
  fetchError: undefined,
};

// reducer
type Action = { type: string; payload?: unknown };
export const reducer: Reducer<State, Action> = (state, { type, payload }) =>
  ({
    [fetching]: {
      ...state,
      loading: true,
    },
    [fetched]: {
      loading: false,
      categories: (payload as Category[]) || state.categories,
    },
    [fetchError]: {
      ...state,
      loading: false,
      fetchError: (payload as Error) || state.fetchError,
    },
  })[type] || state;

// thunks
const fetchRecommendations =
  (dispatch: Dispatch<Action>) => async (user: User) => {
    dispatch({ type: fetching });

    try {
      const response = await fetch("/api/personalizedproductrecommendations", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          // Update body with properties needed for AI model
          emailAddress: user.emailAddress,
        }),
      });
      if (response.ok) {
        const responseData = (await response.json()) as CategoryResponse[];

        const categories = responseData.map((category) => {
          return {
            categoryName: category.categoryName,
            items: category.items.map(
              (item) =>
                ({
                  description: item.description,
                  imagePath: item.imagePath,
                  itemNumber: item.itemNumber,
                  link: item.link,
                  altText: item.altText,
                  price: item.displaySalePrice ? item.salePrice : item.price,
                  unitOfMeasure: item.unitOfMeasure,
                  isWebstaurantPlusEligible: item.isWebstaurantPlusEligible,
                }) as RecommendedItem,
            ),
          };
        });

        dispatch({ type: fetched, payload: categories });
      } else {
        throw new Error(
          `Failed to fetch recommendations: ${response.statusText}`,
        );
      }
    } catch (error) {
      logException(error);
      dispatch({ type: fetchError, payload: error });
    }
  };

export interface PersonalizedProductRecommendationActions {
  fetchRecommendations: ReturnType<typeof fetchRecommendations>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withActions = bindActions<
  State,
  Action,
  PersonalizedProductRecommendationActions
>(
  (dispatch) =>
    ({
      fetchRecommendations: fetchRecommendations(dispatch),
    }) as const,
);
